import axios from 'axios';
import i18n from '../i18n';
import { apiBaseUrl, resolveBackendUrl } from '@/config/apiConfig';

const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

const getActiveLanguage = () => {
  const rawLanguage =
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem('i18nextLng') ||
    'en';

  return rawLanguage.toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

const BASE64_RE = /^[A-Za-z0-9+/=]+$/;

const looksLikeBase64 = (value) => {
  const compact = (value || '').replace(/\s+/g, '');
  return compact.length > 64 && BASE64_RE.test(compact);
};

const decodeBase64 = (value) => {
  try {
    const decoder =
      typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function'
        ? globalThis.atob
        : null;

    return decoder ? decoder(value) : null;
  } catch (_) {
    return null;
  }
};

const detectImageMimeType = (binary) => {
  if (!binary || binary.length < 4) return '';

  const byte = (index) => binary.charCodeAt(index);
  if (byte(0) === 0xff && byte(1) === 0xd8 && byte(2) === 0xff) {
    return 'image/jpeg';
  }
  if (byte(0) === 0x89 && binary.slice(1, 4) === 'PNG') {
    return 'image/png';
  }
  if (binary.startsWith('GIF87a') || binary.startsWith('GIF89a')) {
    return 'image/gif';
  }
  if (binary.length >= 12 && binary.slice(0, 4) === 'RIFF' && binary.slice(8, 12) === 'WEBP') {
    return 'image/webp';
  }

  return '';
};

const normalizeBase64Image = (value) => {
  const compact = (value || '').replace(/\s+/g, '');
  if (!looksLikeBase64(compact)) return null;

  const firstPass = decodeBase64(compact);
  const firstPassMimeType = detectImageMimeType(firstPass);
  if (firstPassMimeType) {
    return { payload: compact, mimeType: firstPassMimeType };
  }

  const nested = (firstPass || '').trim();
  if (!looksLikeBase64(nested)) return null;

  const secondPass = decodeBase64(nested);
  const secondPassMimeType = detectImageMimeType(secondPass);
  if (secondPassMimeType) {
    return { payload: nested.replace(/\s+/g, ''), mimeType: secondPassMimeType };
  }

  return null;
};

export const normalizeImageValue = (value) => {
  if (!value || typeof value !== 'string') return '';
  const image = value.trim();
  if (!image) return '';
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  if (image.startsWith('data:image')) {
    const [, payload = ''] = image.split(',');
    const normalizedImage = normalizeBase64Image(payload);
    return normalizedImage
      ? `data:${normalizedImage.mimeType};base64,${normalizedImage.payload}`
      : image;
  }
  if (image.startsWith('/')) {
    return resolveBackendUrl(image);
  }

  const normalizedImage = normalizeBase64Image(image);
  if (normalizedImage) {
    return `data:${normalizedImage.mimeType};base64,${normalizedImage.payload}`;
  }
  return image;
};

const readCurrencyText = (value) => {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) {
    return readCurrencyText(value[1]) || readCurrencyText(value[0]);
  }
  if (typeof value === 'object') {
    for (const key of ['symbol', 'currency_symbol', 'code', 'name', 'display_name', 'value']) {
      const resolved = readCurrencyText(value[key]);
      if (resolved) return resolved;
    }
    return '';
  }

  return String(value).trim();
};

export const resolveCurrencySymbol = (source = {}, fallback = 'SYP') => {
  const directSymbol =
    readCurrencyText(source?.currency_symbol) ||
    readCurrencyText(source?.currencySymbol);

  if (directSymbol) {
    return directSymbol;
  }

  const currencyCode =
    readCurrencyText(source?.doctor_currency) ||
    readCurrencyText(source?.currency) ||
    readCurrencyText(source?.currency_name) ||
    readCurrencyText(source?.currency_code) ||
    readCurrencyText(source?.currency_id) ||
    fallback;

  const normalizedCode = currencyCode.toUpperCase();
  if (normalizedCode === 'USD' || normalizedCode.includes('US DOLLAR')) {
    return '$';
  }
  if (normalizedCode === 'SYP' || normalizedCode.includes('SYRIAN')) {
    return 'SYP';
  }

  return currencyCode;
};

export const formatCurrencyAmount = (value, currencySource = {}, fallback = 'SYP') => {
  const amount = Number(value || 0);
  const formattedAmount = Number.isFinite(amount)
    ? (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2))
    : '0';
  const currencySymbol = resolveCurrencySymbol(currencySource, fallback);

  if (!currencySymbol) {
    return formattedAmount;
  }

  return /^[A-Za-z]/.test(currencySymbol)
    ? `${currencySymbol} ${formattedAmount}`
    : `${currencySymbol}${formattedAmount}`;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    const language = getActiveLanguage();
    config.headers['Accept-Language'] = language;

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const nonCriticalEndpoints = [
        '/notifications',
        '/wallet',
      ];
      
      const isNonCritical = nonCriticalEndpoints.some(endpoint => url.includes(endpoint));
      const hasToken = !!localStorage.getItem('token');
      
      if (!isNonCritical && hasToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ============================================
// Utility Functions
// ============================================
export const getLocalizedValue = (obj, lang = 'ar') => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj['ar'] || obj['en'] || '';
};

const specialtyFallbackTranslations = {
  'أمراض القلب': {
    ar: 'أمراض القلب',
    en: 'Cardiology',
    description_ar: 'أخصائيو القلب والأوعية الدموية',
    description_en: 'Heart and cardiovascular system specialists',
  },
  'طب العيون': {
    ar: 'طب العيون',
    en: 'Ophthalmology',
    description_ar: 'أخصائيو العناية بالعيون والرؤية',
    description_en: 'Eyes and vision specialists',
  },
  'طب الأعصاب': {
    ar: 'طب الأعصاب',
    en: 'Neurology',
    description_ar: 'أخصائيو الدماغ والجهاز العصبي',
    description_en: 'Brain and nervous system specialists',
  },
  'جراحة العظام': {
    ar: 'جراحة العظام',
    en: 'Orthopedics',
    description_ar: 'أخصائيو العظام والمفاصل والعضلات',
    description_en: 'Bones, joints, and muscles specialists',
  },
  'طب الأطفال': {
    ar: 'طب الأطفال',
    en: 'Pediatrics',
    description_ar: 'أخصائيو صحة الأطفال',
    description_en: 'Children healthcare specialists',
  },
  'الطب العام': {
    ar: 'الطب العام',
    en: 'General Medicine',
    description_ar: 'أطباء الرعاية الأولية',
    description_en: 'Primary care physicians',
  },
  'طب الأسنان': {
    ar: 'طب الأسنان',
    en: 'Dentistry',
    description_ar: 'أخصائيو صحة الفم والأسنان',
    description_en: 'Teeth and oral health care',
  },
  'الأمراض الجلدية': {
    ar: 'الأمراض الجلدية',
    en: 'Dermatology',
    description_ar: 'أخصائيو العناية بالبشرة والشعر والأظافر',
    description_en: 'Skin, hair, and nails care',
  },
  'أمراض النساء': {
    ar: 'أمراض النساء',
    en: 'Gynecology',
    description_ar: 'أخصائيو صحة المرأة',
    description_en: "Women's health specialists",
  },
  'الأنف والأذن والحنجرة (الأذن والأنف والحنجرة)': {
    ar: 'الأنف والأذن والحنجرة (الأذن والأنف والحنجرة)',
    en: 'ENT (Ear, Nose, Throat)',
    description_ar: 'أخصائيو الأذن والأنف والحنجرة',
    description_en: 'Ear, nose, and throat specialists',
  },
  'الجراحة العامة': {
    ar: 'الجراحة العامة',
    en: 'General Surgery',
    description_ar: 'أخصائيو الإجراءات الجراحية',
    description_en: 'Surgical procedures specialists',
  },
  'الطب النفسي': {
    ar: 'الطب النفسي',
    en: 'Psychiatry',
    description_ar: 'أخصائيو الصحة النفسية',
    description_en: 'Mental health specialists',
  },
  'المسالك البولية': {
    ar: 'المسالك البولية',
    en: 'Urology',
    description_ar: 'أخصائيو الجهاز البولي',
    description_en: 'Urinary system specialists',
  },
  'تغذية': {
    ar: 'تغذية',
    en: 'Nutrition',
    description_ar: 'تقييم الحالة التغذوية، فحص المرضى، وتحليل تاريخهم الصحي، وفحص نتائج المختبر لتحديد الاحتياجات الغذائية....',
    description_en: 'Nutritional assessment, patient evaluation, health history review, and lab results analysis to determine dietary needs.',
  },
};

const getSpecialtyFallbackTranslation = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalizedValue = value.trim();
  return specialtyFallbackTranslations[normalizedValue] || null;
};

const normalizeDoctorSpecialty = (doctor, lang = 'ar') => {
  const specialtyCandidates = [
    doctor.specialities?.[0],
    doctor.specialties?.[0],
    doctor.specialty,
    doctor.speciality,
    doctor.specialty_name
      ? { id: doctor.specialty_id, name: doctor.specialty_name }
      : null,
    doctor.speciality_name
      ? { id: doctor.speciality_id || doctor.specialty_id, name: doctor.speciality_name }
      : null,
  ].filter(Boolean);

  const specialtyData = specialtyCandidates[0];

  if (!specialtyData) {
    return { id: undefined, name: '', name_ar: '', name_en: '' };
  }

  if (typeof specialtyData === 'string') {
    return {
      id: doctor.specialty_id || doctor.speciality_id,
      name: specialtyData,
      name_ar: specialtyData,
      name_en: specialtyData,
    };
  }

  const localizedName =
    getLocalizedValue(specialtyData.name, lang) ||
    specialtyData.name ||
    specialtyData.specialty_name ||
    specialtyData.speciality_name ||
    specialtyData.label ||
    '';

  const nameAr =
    getLocalizedValue(specialtyData.name, 'ar') ||
    specialtyData.name_ar ||
    localizedName;

  const nameEn =
    getLocalizedValue(specialtyData.name, 'en') ||
    specialtyData.name_en ||
    localizedName;

  return {
    id:
      specialtyData.id ??
      specialtyData.specialty_id ??
      specialtyData.speciality_id ??
      doctor.specialty_id ??
      doctor.speciality_id,
    name: localizedName,
    name_ar: nameAr,
    name_en: nameEn,
  };
};

// Transform doctor data from Odoo API to frontend format
export const transformDoctor = (doctor, lang = 'ar') => {
  const specialtyData = normalizeDoctorSpecialty(doctor, lang);
  const consultationFee =
    doctor.consultation_price ??
    doctor.consultation_fee ??
    doctor.price ??
    0;
  
  return {
    id: doctor.id,
    name: doctor.name,
    specialty: {
      id: specialtyData.id,
      name: specialtyData.name,
      name_ar: specialtyData.name_ar,
      name_en: specialtyData.name_en,
    },
    rating: doctor.average_rating || doctor.rating || 0,
    reviews_count: doctor.total_reviews || doctor.reviews_count || 0,
    experience_years: doctor.experience_years || 0,
    address: doctor.address || '',
    phone: doctor.phone || '',
    image: normalizeImageValue(
      doctor.image ||
      doctor.image_url ||
      doctor.avatar ||
      doctor.media?.[0]?.url ||
      doctor.media?.[0]?.thumb ||
      doctor.media?.[0]?.icon ||
      ''
    ),
    gender: doctor.gender || doctor.sex || doctor.partner?.gender || '',
    bio: doctor.bio || doctor.description || '',
    consultation_fee: consultationFee,
    doctor_currency: doctor.doctor_currency || doctor.currency || doctor.currency_name || 'SYP',
    currency: doctor.currency || doctor.doctor_currency || doctor.currency_name || 'SYP',
    currency_name: doctor.currency_name || doctor.currency || doctor.doctor_currency || 'SYP',
    currency_symbol: resolveCurrencySymbol(doctor),
    available: doctor.available || false,
    city: doctor.city || '',
    clinic_id: doctor.clinic_id || doctor.clinic?.id || null,
    clinic: doctor.clinic || null,
    enable_online_consultation: doctor.enable_online_consultation || false,
  };
};

// Transform specialty data from Odoo API
export const transformSpecialty = (specialty, lang = 'ar') => {
  const baseName = specialty.name || specialty.specialty_name || specialty.speciality_name || '';
  const fallbackTranslation =
    getSpecialtyFallbackTranslation(getLocalizedValue(specialty.name, 'ar')) ||
    getSpecialtyFallbackTranslation(specialty.name_ar) ||
    getSpecialtyFallbackTranslation(baseName);

  const hasLocalizedNameObject = specialty.name && typeof specialty.name === 'object';

  const nameAr =
    (hasLocalizedNameObject ? getLocalizedValue(specialty.name, 'ar') : '') ||
    specialty.name_ar ||
    fallbackTranslation?.ar ||
    baseName;

  const nameEn =
    (hasLocalizedNameObject ? getLocalizedValue(specialty.name, 'en') : '') ||
    specialty.name_en ||
    fallbackTranslation?.en ||
    baseName;

  const localizedName =
    lang === 'ar'
      ? nameAr
      : nameEn;

  const description =
    lang === 'ar'
      ? (specialty.description_ar || specialty.description || fallbackTranslation?.description_ar || '')
      : (specialty.description_en || fallbackTranslation?.description_en || specialty.description || '');

  return {
    id: specialty.id,
    name: localizedName,
    name_ar: nameAr,
    name_en: nameEn,
    description,
    doctors_count: specialty.doctors_count || specialty.doctor_count || 0,
    image: normalizeImageValue(specialty.image_url || specialty.image),
    color: specialty.color,
  };
};

// ============================================
// Auth Service - Odoo API v1
// ============================================
export const authService = {
  // Patient login - legacy-compatible endpoint.
  // POST /api/v1/login (multipart/form-data)
  loginPatient: (phoneOrEmail, password) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    formData.append('password', password);
    formData.append('partner_type', 'patient');
    return api.post('/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Doctor login - legacy-compatible endpoint.
  // POST /api/v1/doctor/login
  loginDoctor: (phoneOrEmail, password) => {
    return api.post('/doctor/login', {
      phoneandEmail: phoneOrEmail,
      password: password,
      partner_type: 'doctor',
    });
  },

  login: (phoneNumber, password, userType = 'patient') => {
    if (userType === 'doctor') {
      return authService.loginDoctor(phoneNumber, password);
    }
    return authService.loginPatient(phoneNumber, password);
  },

  // Register - legacy-compatible endpoint.
  // POST /api/v1/register (multipart/form-data)
  register: (data) => {
    const formData = new FormData();
    formData.append('name', data.name || '');
    formData.append('email', data.email || '');
    formData.append('phone_number', data.phoneNumber || data.phone || '');
    formData.append('password', data.password || '');
    formData.append('partner_type', data.partnerType || data.partner_type || 'patient');
    if (data.cityId || data.city_id) {
      formData.append('city_id', data.cityId || data.city_id);
    }
    if (data.deviceToken || data.device_token) {
      formData.append('device_token', data.deviceToken || data.device_token);
    }
    if (data.dateOfBirth || data.date_of_birth) {
      formData.append('date_of_birth', data.dateOfBirth || data.date_of_birth);
    }
    if (data.gender) {
      formData.append('gender', data.gender);
    }
    return api.post('/register', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getCities: (governmentId) => {
    const params = governmentId ? { government_id: governmentId } : {};
    return api.get('/cities', { params });
  },

  // OTP: send verification code.
  // POST /api/v1/send-otp
  sendOtp: (phoneOrEmail) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    return api.post('/send-otp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // OTP: verify code.
  // POST /api/v1/check-otp
  verifyOtp: (phoneOrEmail, otpCode) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    formData.append('otp_code', otpCode);
    return api.post('/check-otp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Patient phone verification flow used before saving changed family member phone numbers.
  requestPatientPhoneVerification: (phoneOrEmail) =>
    api.post('/patients/phone-verification/request', {
      phone_number: phoneOrEmail,
      phone: phoneOrEmail,
      phoneandEmail: phoneOrEmail,
    }),

  verifyPatientPhoneVerification: (phoneOrEmail, otpCode) =>
    api.post('/patients/phone-verification/verify', {
      phone_number: phoneOrEmail,
      phone: phoneOrEmail,
      phoneandEmail: phoneOrEmail,
      otp_code: otpCode,
    }),

  // Profile phone update OTP flow.
  sendOtpWithAuth: (phoneOrEmail) => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    return api.post('/send-otp-with-auth', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  verifyOtpNewPhone: (otpCode) => {
    const formData = new FormData();
    formData.append('otp_code', otpCode);
    return api.post('/check-otp-new-phone', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Forgot password flow.
  sendResetLink: (phoneOrEmail, partnerType = 'patient') => {
    const formData = new FormData();
    formData.append('email', phoneOrEmail);
    formData.append('phoneandEmail', phoneOrEmail);
    formData.append('partner_type', partnerType);

    const endpoint =
      partnerType === 'doctor'
        ? '/doctor/send_reset_link_email'
        : partnerType === 'clinic'
          ? '/clinic_owner/send_reset_link_email'
          : '/send_reset_link_email';

    return api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  resetPasswordWithOtp: (phoneOrEmail, otpCode, newPassword, partnerType = 'patient') => {
    const formData = new FormData();
    formData.append('phoneandEmail', phoneOrEmail);
    formData.append('otp_code', otpCode);
    formData.append('password', newPassword);
    formData.append('partner_type', partnerType);
    return api.post('/new-password', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // GET /api/v1/auth/me (fallback compatible endpoint remains /api/v1/user)
  getProfile: () => api.get('/auth/me').catch(() => api.get('/user')),

  // PUT /api/v1/auth/me updates the authenticated account profile.
  updateCurrentUser: (data = {}, config) =>
    api.put('/auth/me', data, config).catch((error) => {
      if (error?.response?.status === 404) {
        return api.put('/user', data, config);
      }
      throw error;
    }),

  // POST /api/v1/users/:id updates account contact fields without touching patient name.
  updateProfile: (userId, data = {}) => {
    const { name, first_name, last_name, display_name, ...contactData } = data;
    return api.post(`/users/${userId}`, contactData);
  },

  // POST /api/v1/logout
  logout: () => api.post('/logout').catch(() => api.post('/auth/logout')),

  // POST /api/v1/auth/change-password
  changePassword: (oldPassword, newPassword) => api.post('/auth/change-password', {
    old_password: oldPassword,
    new_password: newPassword,
  }),
};

// ============================================
// Doctor Service - Odoo API v1
// ============================================
const normalizeDoctorPayload = (response) => {
  const payload = response?.data?.data || response?.data || {};
  const doctor = payload?.doctor || payload;
  return {
    ...response,
    data: {
      ...response.data,
      data: doctor,
    },
  };
};

const readMap = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const readList = (value) => (Array.isArray(value) ? value : []);

const readString = (value) => {
  if (value == null || value === false) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'value', 'ar', 'en']) {
      const resolved = readString(value[key]);
      if (resolved) return resolved;
    }
    return null;
  }

  const normalized = String(value).trim();
  return normalized || null;
};

const readInt = (value) => {
  if (value == null || value === false) return null;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const isFileLike = (value) => (
  (typeof File !== 'undefined' && value instanceof File) ||
  (typeof Blob !== 'undefined' && value instanceof Blob)
);

const isFormDataPayload = (value) => (
  typeof FormData !== 'undefined' && value instanceof FormData
);

const toProfileFormData = (payload = {}) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null) formData.append(`${key}[]`, item);
      });
      return;
    }
    if (value instanceof Date) {
      formData.append(key, value.toISOString().slice(0, 10));
      return;
    }
    formData.append(key, value);
  });

  return formData;
};

const profileRequestConfig = (payload) => (
  isFormDataPayload(payload)
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : undefined
);

const extractProfileEntity = (payload) => {
  const response = readMap(payload);
  const data = readMap(response.data);

  const candidates = [
    readMap(data.profile),
    readMap(data.user),
    data,
    readMap(response.profile),
    readMap(response.user),
    response,
  ];

  return candidates.find((item) => Object.keys(item).length > 0) || {};
};

const splitFullName = (fullName = '') => {
  const parts = String(fullName || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean);

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

const buildPatientProfilePayload = (data = {}) => {
  const imageFile =
    data.imageFile ||
    data.image_file ||
    (isFileLike(data.image) ? data.image : null);
  const removeImage = data.remove_image === true || data.removeImage === true;
  const fullName =
    readString(data.name) ||
    [readString(data.first_name), readString(data.last_name)]
      .filter(Boolean)
      .join(' ');
  const splitName = splitFullName(fullName);
  const phone = readString(data.phone) || readString(data.phone_number);
  const email = readString(data.email);
  const payload = {
    ...data,
    name: fullName,
    first_name: readString(data.first_name) || splitName.firstName,
    last_name: readString(data.last_name) || splitName.lastName,
    partner_type: readString(data.partner_type) || 'patient',
  };

  if (email) {
    payload.email = email;
  } else {
    delete payload.email;
  }

  delete payload.imageFile;
  delete payload.image_file;
  delete payload.removeImage;

  if (phone) {
    payload.phone = phone;
    payload.phone_number = phone;
  } else {
    delete payload.phone;
    delete payload.phone_number;
  }

  if (removeImage) {
    payload.remove_image = true;
    delete payload.image;
  } else if (imageFile) {
    payload.image = imageFile;
    delete payload.remove_image;
  } else if (readString(data.image)) {
    payload.image = readString(data.image);
  } else {
    delete payload.image;
  }

  return imageFile ? toProfileFormData(payload) : payload;
};

const looksLikeProfileData = (data) => (
  readInt(data?.id) != null ||
  readString(data?.name) != null ||
  readString(data?.email) != null ||
  readString(data?.phone) != null ||
  readString(data?.phone_number) != null ||
  readString(data?.mobile) != null
);

const mergeProfileData = (primary = {}, overlay = {}) => {
  const merged = { ...readMap(primary) };
  Object.entries(readMap(overlay)).forEach(([key, value]) => {
    if (value == null || value === false) return;
    if (typeof value === 'string' && value.trim() === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
      return;
    }
    merged[key] = value;
  });

  return merged;
};

const assertSuccessfulProfileResponse = (response, fallbackMessage) => {
  const payload = readMap(response?.data);
  if (payload.success === false) {
    throw new Error(
      readString(payload.message) ||
      readString(payload.error) ||
      fallbackMessage
    );
  }
};

const normalizePatientProfileResponse = (response, profile) => {
  const payload = readMap(response?.data);
  const normalizedProfile = Object.keys(readMap(profile)).length > 0
    ? readMap(profile)
    : extractProfileEntity(payload);

  return {
    ...response,
    data: {
      ...payload,
      success: payload.success !== false,
      data: normalizedProfile,
    },
  };
};

const shouldRetryProfileFallback = (error) => {
  const status = error?.response?.status;
  return status === 404 || status === 405;
};

const normalizeDoctorProfilePayload = (response) => {
  const payload = readMap(response?.data);
  const doctor = extractProfileEntity(payload);

  return {
    ...response,
    data: {
      ...payload,
      data: doctor,
    },
  };
};

export const doctorService = {
  // GET /api/v1/doctors
  getAll: (params) => api.get('/doctors', { params }),
  getAllDoctors: (params) => api.get('/doctors', { params }),
  
  // GET /api/v1/doctors/:id
  getById: (id) => api.get(`/doctors/${id}`).then(normalizeDoctorPayload),
  getProfile: () =>
    api
      .get('/doctor/profile')
      .then(normalizeDoctorProfilePayload)
      .catch(() => authService.getProfile().then(normalizeDoctorProfilePayload)),
  updateProfile: (id, data) =>
    api.put(`/doctors/${id}`, data).then(normalizeDoctorProfilePayload),
  
  // Filter by specialty
  getBySpecialty: (specialtyId) => api.get('/doctors', { 
    params: { specialty_id: specialtyId } 
  }),
  
  // POST /api/v1/doctors/search
  search: (query) => api.post('/doctors/search', { query }),
  
  // GET /api/v1/doctors/:id/reviews
  getReviews: (doctorId) => api.get(`/doctors/${doctorId}/reviews`),
  
  // POST /api/v1/appointments/available-slots
  getAvailableSlots: (doctorId, date, doctorServiceId = null, appointmentType = 'regular') => api.post('/appointments/available-slots', {
    doctor_id: doctorId,
    date: date,
    appointment_type: appointmentType,
    ...(doctorServiceId ? { doctor_service_id: doctorServiceId } : {}),
  }),
  
  // Legacy availability endpoint used by booking UI: returns [[datetime, isAvailable, isBooked], ...]
  getAvailabilityHours: (doctorId, date, doctorServiceId = null) => api.get(`/availability_hours/${doctorId}`, {
    params: {
      date,
      ...(doctorServiceId ? { doctor_service_id: doctorServiceId } : {}),
    },
  }),
  getServices: (doctorId) => api.get(`/doctors/${doctorId}/services`),
  getServiceChildren: (serviceId) => api.get(`/doctor-services/${serviceId}/children`),
  getServiceSlots: (serviceId, date) => api.get(`/doctor-services/${serviceId}/slots`, { params: { date } }),
  
  // Add doctor review. Mirrors the mobile app endpoint so reviews are tied to appointments.
  addReview: (data) => api.post('/doctor_reviews', data),
};

// ============================================
// Specialty Service - Odoo API v1
// ============================================
export const specialtyService = {
  // GET /api/v1/specialities
  getAll: () => api.get('/specialities'),
  getById: (id) => api.get(`/specialities/${id}`),
};

// ============================================
// Appointment Service - Odoo API v1
// ============================================
const APPOINTMENT_STATUS_TO_ID = {
  draft: 1,
  confirmed: 2,
  in_progress: 3,
  completed: 4,
  cancelled: 5,
  no_show: 6,
  waiting: 7,
};

const normalizeAppointmentEntity = (appointment = {}) => {
  const normalized = { ...appointment };
  if (!normalized.appointment_status_id && normalized.state) {
    normalized.appointment_status_id = APPOINTMENT_STATUS_TO_ID[normalized.state] || 1;
  }
  if (!normalized.appointment_at) {
    normalized.appointment_at = normalized.appointment_date || normalized.start_at || null;
  }
  if (!normalized.start_at) {
    normalized.start_at = normalized.appointment_at || normalized.appointment_date || null;
  }
  if (!normalized.ends_at && normalized.end_datetime) {
    normalized.ends_at = normalized.end_datetime;
  }
  if (normalized.total == null && normalized.price != null) {
    normalized.total = normalized.price;
  }
  return normalized;
};

const normalizeAppointmentsResponse = (response) => {
  const payload = response?.data?.data;
  let items = [];
  if (Array.isArray(payload)) {
    items = payload;
  } else if (payload && Array.isArray(payload.appointments)) {
    items = payload.appointments;
  } else if (Array.isArray(response?.data)) {
    items = response.data;
  }
  const normalized = items.map(normalizeAppointmentEntity);
  return {
    ...response,
    data: {
      ...response.data,
      data: normalized,
    },
  };
};

export const appointmentService = {
  // GET /api/v1/appointments
  getMyAppointments: (status) =>
    api
      .get('/appointments', {
        params: status ? { status } : {},
      })
      .then(normalizeAppointmentsResponse),
  
  getAllAppointments: () => api.get('/appointments').then(normalizeAppointmentsResponse),
  
  // GET /api/v1/appointments/:id
  getById: (id) =>
    api.get(`/appointments/${id}`).then((response) => {
      const payload = response?.data?.data || {};
      const appointment = normalizeAppointmentEntity(payload?.appointment || payload);
      return {
        ...response,
        data: {
          ...response.data,
          data: appointment,
        },
      };
    }),
  
  // POST /api/v1/appointments
  book: (data) => api.post('/appointments', {
    doctor_id: data.doctor_id || data.doctor?.id,
    patient_id: data.patient_id || data.patient?.id,
    doctor_service_id: data.doctor_service_id || data.service_id || data.doctor_service?.id,
    clinic_id: data.clinic_id || data.clinic?.id || data.doctor?.clinic_id,
    appointment_date: data.appointment_date || data.appointment_at || data.start_at,
    appointment_type: data.appointment_type || (data.is_waiting_appointment ? 'waiting' : (data.online ? 'online' : 'regular')),
    chief_complaint: data.chief_complaint || data.reason || '',
    notes: data.notes || data.hint || '',
    duration: data.duration,
    ends_at: data.ends_at,
    online: !!data.online,
    is_waiting_appointment: !!data.is_waiting_appointment,
    auto_confirm: data.auto_confirm !== false,
  }),
  
  // POST /api/v1/appointments/:id/cancel
  cancel: (id) => api.post(`/appointments/${id}/cancel`),

  // PUT /api/v1/appointments/:id
  // Match the mobile app flow, which updates appointment state through the main update endpoint.
  updateStatus: (id, status) => {
    const normalizedStatus = String(status || '').trim().toLowerCase();

    return api.put(`/appointments/${id}`, {
      status: normalizedStatus,
    });
  },

  // POST /api/v1/appointments/:id/accept
  accept: (id) => api.post(`/appointments/${id}/accept`),

  // POST /api/v1/appointments/:id/in-clinic
  markInClinic: (id) => api.post(`/appointments/${id}/in-clinic`),

  // POST /api/v1/appointments/:id/complete
  complete: (id) => api.post(`/appointments/${id}/complete`),
  
  // Reschedule (update appointment date)
  reschedule: (id, data) => api.put(`/appointments/${id}`, {
    appointment_date: data.appointment_date || data.start_at
  }),
};

// ============================================
// Medical Record Service - Odoo API v1
// ============================================
export const medicalRecordService = {
  // GET /api/v1/patients/medical-history
  getHistory: () => api.get('/patients/medical-history'),
  getHistoryOptions: () => api.get('/patients/medical-history/options'),
  
  // Medical reference data
  getChronicDiseases: () => api.get('/medical/chronic-diseases'),
  getMedications: () => api.get('/medical/medications'),
  getPermanentMedications: () => api.get('/medical/medications'),
  getGeneticDiseases: () => api.get('/medical/genetic-diseases'),
  getSurgeries: () => api.get('/medical/surgeries'),
  getVaccines: () => api.get('/medical/vaccines'),
  getAllergies: () => api.get('/medical/allergies').catch(() => ({ data: { data: [] } })),
  
  // Get patient's medical history (for doctors)
  getPatientMedicalRecord: (patientId) => api.get(`/patients/${patientId}/medical-history`),
  
  // Create/update medical history
  createMedicalHistory: (data) => api.post('/patients/medical-history', data),
  updateMedicalHistory: (id, data) => api.put(`/patients/medical-history/${id}`, data),
  updatePatientMedicalRecord: (patientId, data) => api.put(`/patients/${patientId}/medical-history`, data),
};

export const medicalService = medicalRecordService;

// ============================================
// Patient Service - Odoo API v1
// ============================================
export const patientService = {
  // Legacy compatibility method used by AuthContext after login.
  // Odoo does not expose Laravel-style /patients?search=user_id:<id> endpoint.
  // We mirror the mobile app flow: read profile + family members and normalize
  // several possible response shapes exposed by the backend.
  getByUserId: async () => {
    const readMap = (value) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
      }
      return {};
    };

    const readList = (value) => (Array.isArray(value) ? value : []);

    const readString = (value) => {
      if (value == null || value === false) return null;
      if (typeof value === 'object' && !Array.isArray(value)) {
        for (const key of ['name', 'display_name', 'en', 'ar', 'value']) {
          const resolved = readString(value[key]);
          if (resolved) return resolved;
        }
        return null;
      }

      const normalized = String(value).trim();
      return normalized || null;
    };

    const readInt = (value) => {
      if (value == null || value === false) return null;
      const parsed = Number.parseInt(String(value), 10);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const readBool = (value, fallback = false) => {
      if (value == null) return fallback;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;

      const normalized = String(value).trim().toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
      if (['false', '0', 'no', 'n', ''].includes(normalized)) return false;
      return fallback;
    };

    const extractRows = (
      value,
      keys = ['data', 'items', 'records', 'results', 'family_members']
    ) => {
      if (Array.isArray(value)) return value;

      if (value && typeof value === 'object') {
        for (const key of keys) {
          if (!(key in value)) continue;
          const rows = extractRows(value[key], keys);
          if (rows.length > 0 || Array.isArray(value[key])) {
            return rows;
          }
        }

        return [value];
      }

      return [];
    };

    const normalizePatient = (patient, fallbackDefault = false) => {
      const item = readMap(patient);
      const id = readInt(item.id);
      if (!id) return null;
      if (readBool(item.hidden_from_patient_lists)) return null;

      const rawName = readString(item.name) || '';
      const firstName =
        readString(item.first_name) ||
        readString(item.firstname) ||
        rawName.split(' ').filter(Boolean).slice(0, -1).join(' ') ||
        rawName;
      const lastName =
        readString(item.last_name) ||
        readString(item.lastname) ||
        rawName.split(' ').filter(Boolean).slice(1).join(' ');

      return {
        ...item,
        id,
        user_id: readInt(item.user_id),
        first_name: firstName || '',
        last_name: lastName || '',
        name: rawName || `${firstName || ''} ${lastName || ''}`.trim(),
        is_default: readBool(item.is_default, fallbackDefault),
      };
    };

    const patientsById = new Map();

    const addPatient = (rawPatient, fallbackDefault = false) => {
      const patient = normalizePatient(rawPatient, fallbackDefault);
      if (!patient) return;

      const existing = patientsById.get(patient.id);
      if (!existing || (!existing.is_default && patient.is_default)) {
        patientsById.set(patient.id, patient);
      }
    };

    try {
      const profileResponse = await api.get('/patients/profile');
      const profilePayload = readMap(profileResponse?.data);
      if (profilePayload.success === true) {
        const profileData = readMap(profilePayload.data);
        addPatient(
          Object.keys(readMap(profileData.profile)).length > 0
            ? readMap(profileData.profile)
            : profileData,
          true
        );
      }
    } catch (error) {}

    try {
      const familyResponse = await api.get('/patients/family-members');
      const familyPayload = readMap(familyResponse?.data);
      if (familyPayload.success === true) {
        const familyData = readMap(familyPayload.data);
        const familyRows = extractRows(
          familyData.family_members ?? familyData,
          ['family_members', 'data', 'items', 'records', 'results']
        );

        familyRows.forEach((item) => addPatient(item));
      }
    } catch (error) {}

    const patients = Array.from(patientsById.values()).sort((a, b) => {
      if (a.is_default !== b.is_default) {
        return a.is_default ? -1 : 1;
      }
      return (a.name || '').localeCompare(b.name || '');
    });

    return { data: { data: patients } };
  },

  // GET /api/v1/patients/profile + GET /api/v1/auth/me
  getProfile: async () => {
    let patientData = null;
    let currentUserData = null;
    let lastError = null;

    try {
      const response = await api.get('/patients/profile');
      assertSuccessfulProfileResponse(response, 'Failed to load profile');
      patientData = extractProfileEntity(response?.data);
    } catch (error) {
      if (!shouldRetryProfileFallback(error)) {
        throw error;
      }
      lastError = error;
    }

    try {
      const response = await authService.getProfile();
      assertSuccessfulProfileResponse(response, 'Failed to load profile');
      currentUserData = extractProfileEntity(response?.data);
    } catch (error) {
      if (!patientData || !shouldRetryProfileFallback(error)) {
        throw error;
      }
      lastError = lastError || error;
    }

    const merged = mergeProfileData(patientData || {}, currentUserData || {});

    if (!looksLikeProfileData(merged)) {
      throw lastError || new Error('Failed to load profile');
    }

    return normalizePatientProfileResponse({ data: { success: true } }, merged);
  },
  
  // PUT /api/v1/patients/profile + PUT /api/v1/auth/me
  updateProfile: async (data = {}) => {
    const payload = buildPatientProfilePayload(data);
    const config = profileRequestConfig(payload);
    let lastError = null;
    let hasSuccessfulUpdate = false;

    for (const updateProfile of [
      () => api.put('/patients/profile', payload, config),
      () => authService.updateCurrentUser(payload, config),
    ]) {
      try {
        const response = await updateProfile();
        assertSuccessfulProfileResponse(response, 'Failed to update profile');
        hasSuccessfulUpdate = true;
      } catch (error) {
        if (!shouldRetryProfileFallback(error)) {
          throw error;
        }
        lastError = error;
      }
    }

    if (!hasSuccessfulUpdate) {
      throw lastError || new Error('Failed to update profile');
    }

    return patientService.getProfile();
  },

  // DELETE /api/v1/patients/profile
  deleteAccount: () => api.delete('/patients/profile'),
  
  // GET /api/v1/patients/family-members
  getAll: () => api.get('/patients/family-members'),
  
  getById: (id) => api.get(`/patients/family-members/${id}`),
  
  // POST /api/v1/patients/family-members
  create: (data) => api.post('/patients/family-members', data),
  
  // PUT /api/v1/patients/family-members/:id
  update: (id, data) => api.put(`/patients/family-members/${id}`, data),
  
  // DELETE /api/v1/patients/family-members/:id
  delete: (id) => api.delete(`/patients/family-members/${id}`),
};

// ============================================
// Notification Service - Odoo API v1
// ============================================
export const notificationService = {
  // GET /api/v1/notifications
  getAll: (params) => api.get('/notifications', { params }),
  
  // GET /api/v1/notifications/count
  getCount: () => api.get('/notifications/count'),
  
  // PUT /api/v1/notifications/:id/read
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  
  // PUT /api/v1/notifications/read-all
  markAllAsRead: () => api.put('/notifications/read-all'),
  
  // DELETE /api/v1/notifications/:id
  delete: (id) => api.delete(`/notifications/${id}`),
};

// ============================================
// Wallet Service - Odoo API v1
// ============================================
export const walletService = {
  // GET /api/v1/wallet
  getBalance: () => api.get('/wallet'),
  
  // GET /api/v1/wallet/transactions
  getTransactions: () => api.get('/wallet/transactions'),
  
  // POST /api/v1/wallet/add-funds
  addFunds: (data) => api.post('/wallet/add-funds', data),
};

// ============================================
// Favorites Service - Odoo API v1
// ============================================
export const favoriteService = {
  // GET /api/v1/patients/favorites
  getAll: () => api.get('/patients/favorites'),
  
  // POST /api/v1/patients/favorites
  add: (doctorId) => api.post('/patients/favorites', { doctor_id: doctorId }),
  
  // DELETE /api/v1/patients/favorites/:doctorId
  remove: (doctorId) => api.delete(`/patients/favorites/${doctorId}`),
  
  // Toggle favorite
  toggle: (doctorId) => api.post('/patients/favorites/toggle', { doctor_id: doctorId }),
};

// ============================================
// Messages/Chat Service - Odoo API v1
// ============================================
export const messageService = {
  getConversations: () => api.get('/messages'),
  getMessages: (conversationId) => api.get(`/messages/${conversationId}`),
  send: (data) => api.post('/messages', data),
  markAsRead: (conversationId) => api.put(`/messages/${conversationId}/read`),
};

// ============================================
// Clinic Service - Odoo API v1
// ============================================
export const clinicService = {
  // GET /api/v1/clinics
  getAll: (params) => api.get('/clinics', { params }),
  getAllClinics: (params) => api.get('/clinics', { params }),
  
  // GET /api/v1/clinics/:id
  getById: (id) => api.get(`/clinics/${id}`),
  
  // GET /api/v1/clinics/:id/doctors
  getDoctors: (clinicId) => api.get(`/clinics/${clinicId}/doctors`),
  
  // GET /api/v1/clinics/:id/reviews
  getReviews: (clinicId) => api.get(`/clinics/${clinicId}/reviews`),
  
  // POST /api/v1/clinics/reviews
  addReview: (data) => api.post('/clinics/reviews', data),
};

export const hospitalService = {
  getAll: (params) => api.get('/hospitals', { params }),
  getAllHospitals: (params) => api.get('/hospitals', { params }),
};

// ============================================
// Pharmacy Service - Odoo API v1
// ============================================
export const pharmacyService = {
  getAll: (params) => api.get('/pharmacies', { params }),
  getById: (id) => api.get(`/pharmacies/${id}`),
  getProducts: (params) => api.get('/pharmacy/products', { params }),
  getProductById: (id) => api.get(`/pharmacy/products/${id}`),
  getCategories: () => api.get('/pharmacy/categories'),
  getOrders: () => api.get('/pharmacy/orders'),
  getOrderById: (id) => api.get(`/pharmacy/orders/${id}`),
  createOrder: (data) => api.post('/pharmacy/orders', data),
};

// ============================================
// Family Members Service - Odoo API v1
// ============================================
export const familyService = {
  getAll: () => api.get('/patients/family-members'),
  add: (data) => api.post('/patients/family-members', data),
  update: (id, data) => api.put(`/patients/family-members/${id}`, data),
  delete: (id) => api.delete(`/patients/family-members/${id}`),
};

// ============================================
// Content Service - Odoo API v1
// ============================================
export const contentService = {
  getTermsOfUse: () => api.get('/content/terms'),
  getPrivacyPolicy: () => api.get('/content/privacy-policy'),
  getDoctorPrivacyPolicy: () => api.get('/content/doctor-privacy-policy'),
  getCustomPages: () => api.get('/content/pages'),
  getFaqs: () => api.get('/content/faqs'),
  getFaqCategories: () => api.get('/content/faq-categories'),
  getSettings: () => api.get('/content/settings'),
};

// ============================================
// Medicine/Product Service - Odoo API v1
// ============================================
export const medicineService = {
  getAll: (params) => api.get('/pharmacy/products', { params }),
  getById: (id) => api.get(`/pharmacy/products/${id}`),
  getCategories: () => api.get('/pharmacy/categories'),
  search: (query) => api.get('/pharmacy/products', { params: { search: query } }),
};

// ============================================
// Address Service - Odoo API v1
// ============================================
export const addressService = {
  getAll: () => api.get('/patients/addresses'),
  getById: (id) => api.get(`/patients/addresses/${id}`),
  create: (data) => api.post('/patients/addresses', data),
  update: (id, data) => api.put(`/patients/addresses/${id}`, data),
  delete: (id) => api.delete(`/patients/addresses/${id}`),
};

// ============================================
// Coupon Service - Legacy parity
// ============================================
export const couponService = {
  getAll: () => api.get('/coupons'),
  validate: (code) => api.get('/coupons', { params: { code } }),
  create: (data) => api.post('/coupons', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  delete: (id) => api.delete(`/coupons/${id}`),
};

// ============================================
// Complaint Service - Legacy parity
// ============================================
export const complaintService = {
  create: (data) => api.post('/complaints', data),
  getMine: () => api.get('/get-complaints'),
};

// ============================================
// FAQ Service - Odoo API v1
// ============================================
export const faqService = {
  getAll: () => api.get('/content/faqs'),
  getCategories: () => api.get('/content/faq-categories'),
};

// ============================================
// Slides/Banner Service - Odoo API v1
// ============================================
export const slideService = {
  getAll: () => api.get('/content/slides'),
};

// ============================================
// Legacy Reference Service - parity helpers
// ============================================
export const referenceService = {
  getAppointmentStatuses: () => api.get('/appointment_statuses'),
  getPaymentMethods: () => api.get('/payment_methods'),
  getPredefinedReviews: (params) => api.get('/predefined_reviews', { params }),
  getClinicLevels: () => api.get('/clinic_levels'),
  getHospitalLevels: () => api.get('/hospital_levels'),
  getTaxes: () => api.get('/taxes'),
  getInsuranceCompanies: () => api.get('/insurance-companies'),
};
